type SchemaLike = { scoringEnabled?: boolean | null } | null | undefined;

type EditionLike =
  | {
      settings?: {
        judging?: {
          scoringEnabled?: boolean | null;
          hidePointsOnScoresheet?: boolean | null;
        } | null;
      } | null;
    }
  | null
  | undefined;

export type ScoringSources = {
  /** Scoresheet schema, e.g. `scoresheet.scoresheetSchema?.data`. Wins over the edition. */
  schema?: SchemaLike;
  /** Contests edition, e.g. `entry.contestsEdition?.data`. */
  edition?: EditionLike;
};

/**
 * Whether scoresheets are evaluated with points (section points, score, consensus score).
 *
 * The scoresheet schema decides when it is available, otherwise the edition's judging
 * setting does (every schema of an edition shares the edition's mode). Anything unknown
 * means points, which is how all contests worked before scoring could be switched off.
 */
export const isScoringEnabled = ({
  schema,
  edition,
}: ScoringSources = {}): boolean => {
  if (typeof schema?.scoringEnabled === 'boolean') {
    return schema.scoringEnabled;
  }

  const editionSetting = edition?.settings?.judging?.scoringEnabled;

  return typeof editionSetting === 'boolean' ? editionSetting : true;
};

/**
 * Whether points should be rendered for participants: scoring is on and the organizer
 * did not hide the points on the scoresheet.
 */
export const shouldShowPoints = (sources: ScoringSources = {}): boolean =>
  isScoringEnabled(sources) &&
  !sources.edition?.settings?.judging?.hidePointsOnScoresheet;

type CommentRequirement = {
  /** `commentRequiredBelowPercent` of the scoresheet schema. */
  thresholdPercent?: number | null;
  /** Max points of the section. */
  maxPoints?: number | null;
  /** Points given to the section so far (string or number, empty means none yet). */
  points?: number | string | null;
};

/**
 * Whether a section scored below the schema's threshold needs a comment: its points are
 * below `thresholdPercent` of its max points. Sections without points entered yet are not
 * checked. Mirrors the server-side `comment_required` validation.
 */
export const isCommentRequired = ({
  thresholdPercent,
  maxPoints,
  points,
}: CommentRequirement): boolean => {
  if (!thresholdPercent || !maxPoints || maxPoints <= 0) return false;
  if (points === undefined || points === null || points === '') return false;

  const numericPoints = Number(points);

  if (Number.isNaN(numericPoints)) return false;

  return numericPoints * 100 < thresholdPercent * maxPoints;
};

/** Lowest section points that need no comment, e.g. 9 for 70% of 12 points. */
export const minPointsWithoutComment = (
  thresholdPercent: number,
  maxPoints: number,
): number => Math.ceil((thresholdPercent * maxPoints) / 100);
